// #2121-spec-kits
import React, { useEffect, useState } from 'react';
import {
  Card, CardBody, Label, Form, Input, Button, Badge, UncontrolledTooltip,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { 
  actionAssetSubmitForm, 
  actionAssetSubmitEdition, 
  actionSetEditAsset, 
  actionGetAssetStudyTypes,
  actionGetAssetTypes,
  actionGetAssetBrands
} from 'store/actions/assetsActions';
import { translate } from 'react-translate';
import { toast } from 'react-toastify';
import useAssetStateMachine from 'hooks/useAssetStateMachine';
import moment from 'moment';
import LoadingLocal from 'plugins/TS-lib-utils-public/components/Loading/LoadingLocal';

const STATUS_TONE = {
  a_st_available:    'success',
  a_st_reserved:     'info',
  a_st_assigned:     'primary',
  a_st_unavailable:  'secondary',
  a_st_deleted:      'danger',
};

const TYPE_ICON = {
  cellphone:  'now-ui-icons tech_mobile',
  device:     'now-ui-icons objects_diamond',
  'sim card': 'now-ui-icons tech_tv',
};

const safeJsonParse = (s) => {
  if (!s) return null;
  try { return JSON.parse(s); } catch { return null; }
};

// Mapea los `event` crudos del backend a claves i18n amigables.
const EVENT_KEY = {
  'Asset data updated': 'ev-data-updated',
  'Asset assigned to the kit': 'ev-assigned-kit',
  'Asset removed from the kit': 'ev-removed-kit',
  'Asset registrado': 'ev-registered',
  'Asset registered': 'ev-registered',
};

// El backend audita cambios de atributos como pares old_<campo>/new_<campo>
// (AssetsModel.updateAsset). Mapeamos el nombre del campo a su clave i18n.
const FIELD_LABEL_KEY = {
  asset_type: 'type',
  serial_number: 'serial',
  study_type: 'study-type',
  brand: 'brand',
  model: 'model',
  identifier: 'identifier',
  features: 'features',
};

// Extrae los cambios de campos auditados del log (excluye status: ya se muestra
// como transición). Devuelve [{ field, label, old, new }].
const extractFieldChanges = (log, t) => {
  const changes = [];
  Object.keys(log).forEach((k) => {
    if (!k.startsWith('new_')) return;
    const field = k.slice(4);
    if (field === 'status') return; // cubierto por la transición de estado
    const labelKey = FIELD_LABEL_KEY[field] || field;
    changes.push({
      field,
      label: t(labelKey),
      old: log[`old_${field}`],
      new: log[k],
    });
  });
  return changes;
};

const ActivityTimeline = ({ tracking, t }) => {
  if (!tracking || !tracking.length) {
    return <div className="kit-detail-empty">{t('no-activity')}</div>;
  }
  // ordenamos descendiendo (más reciente arriba)
  const items = [...tracking].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
  return (
    <ul className="kit-timeline kit-timeline--scroll">
      {items.map((entry, i) => {
        const log = safeJsonParse(entry.log) || {};
        // Título amigable: cambio de estado → label del estado; sino, evento traducido.
        const evKey = log.event && EVENT_KEY[log.event];
        const headline = entry.statusId
          ? t(entry.statusId)
          : (evKey ? t(evKey) : (log.event || t('ev-generic')));
        // Transición legible (estados traducidos), no los códigos crudos.
        const fromS = log.oldStatus || log.old_status;
        const toS = log.status || log.new_status || entry.statusId;
        const showTransition = !!fromS && !!toS;
        // Usuario combinado, sin volcar keys técnicas.
        const userName = [log.user_first_name, log.user_last_name].filter(Boolean).join(' ').trim();
        // Cambios de atributos auditados (type/brand/model/serial/etc.).
        const fieldChanges = extractFieldChanges(log, t);
        return (
          <li key={`${entry._id || i}`} className="kit-timeline-item tone-info">
            <div className="kit-timeline-dot" />
            <div className="kit-timeline-card">
              <div className="kit-timeline-meta">
                <strong>{headline}</strong>
                <time>{moment(entry.date).format('DD-MM-YYYY HH:mm:ss')}</time>
              </div>
              {showTransition && (
                <div className="kit-timeline-transition">
                  <span className="kit-tl-chip">{t(fromS)}</span>
                  <i className="now-ui-icons arrows-1_minimal-right" aria-hidden="true" />
                  <span className="kit-tl-chip kit-tl-chip--to">{t(toS)}</span>
                </div>
              )}
              {fieldChanges.length > 0 && (
                <div className="kit-timeline-changes">
                  {fieldChanges.map((c) => (
                    <div className="kit-tl-change" key={c.field}>
                      <span className="kit-tl-field">{c.label}</span>
                      <span className="kit-tl-chip">{c.old || '—'}</span>
                      <i className="now-ui-icons arrows-1_minimal-right" aria-hidden="true" />
                      <span className="kit-tl-chip kit-tl-chip--to">{c.new || '—'}</span>
                    </div>
                  ))}
                </div>
              )}
              {userName && (
                <div className="kit-timeline-user">
                  {t('activity-by')} <span>{userName}</span>
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
};


const AssetsForm = (props) => {
  const dispatch = useDispatch();
  const { t } = props;
  const history = useHistory();

  const assetData    = useSelector((s) => s.assetsReducer.assetEdit);
  const assetLoading = useSelector((s) => s.assetsReducer.assetLoading);
  const studyTypes        = useSelector((s) => s.assetsReducer.studyTypes);
  const types        = useSelector((s) => s.assetsReducer.types);
  const brands       = useSelector((s) => s.assetsReducer.brands);
  const stateMachine = useAssetStateMachine();

  const [assetType, setAssetType] = useState(assetData.asset_type || 'cellphone');
  const [tab, setTab] = useState('info');

  const [selectedBrand, setSelectedBrand] = useState(brands.length ? Object.keys(assetData).length ? brands.find(b => b.name === assetData.brand) : brands[0] : {});

  const { register, reset, handleSubmit } = useForm({
    defaultValues: {
      asset_type:    assetData.asset_type,
      study_type:    assetData.study_type,
      serial_number: assetData.serial_number,
      brand:         assetData.brand,
      model:         assetData.model,
      identifier:    assetData.identifier,
      features:      assetData.features,
      returnable:    assetData.returnable,
    },
  });

  const onSubmit = (data) => {
    if (Object.keys(assetData).length) {
      dispatch(actionAssetSubmitEdition({ ...data, assetId: assetData._id }));
    } else {
      dispatch(actionAssetSubmitForm({ ...data }));
    }
  };

  useEffect(() => {
    if (!Object.keys(studyTypes).length) dispatch(actionGetAssetStudyTypes());
    //return () => { dispatch(actionSetEditAsset({})); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!Object.keys(types).length) dispatch(actionGetAssetTypes());
    document.getElementById("full-page").scrollTo(0,0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!brands.length) {
      dispatch(actionGetAssetBrands());
    } else {
      setSelectedBrand(brands.length ? Object.keys(assetData).length ? brands.find(b => b.name === assetData.brand) : brands[0] : {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brands]);

  useEffect(() => {
    if (!assetLoading && Object.keys(assetData).length && !assetData.is_editable && (typeof assetData.readOnly === "undefined" || assetData.readOnly === false)) history.goBack();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetData, assetLoading]);

  useEffect(() => {
    reset({
      asset_type:    assetData.asset_type,
      study_type:    assetData.study_type,
      serial_number: assetData.serial_number,
      brand:         assetData.brand,
      model:         assetData.model,
      identifier:    assetData.identifier,
      features:      assetData.features,
      returnable:    assetData.returnable,
    })
  }, [assetLoading]);

  const isEditing = !!Object.keys(assetData).length;
  const tone      = isEditing ? (STATUS_TONE[assetData.status] || 'secondary') : 'secondary';
  const icon      = TYPE_ICON[isEditing ? assetData.asset_type : assetType] || 'now-ui-icons objects_diamond';

  const copyId = (e, val) => {
    e.preventDefault();
    navigator.clipboard.writeText(val);
    toast.success(t('id-copied') || 'Copied');
  };

  if (assetLoading) {
    return <LoadingLocal />
  }

  return (
    <div className="content kit-form-page asset-form-page mt-3">

      {/* Breadcrumb */}
      <div className="kit-form-breadcrumb">
        <button
          type="button"
          className="kit-detail-back"
          onClick={(e) => { e.preventDefault(); history.goBack(); }}
        >
          <i className="now-ui-icons arrows-1_minimal-left" />
          <span>{assetData.readOnly ? t('go-back') : t('back-to-list') || 'Back to list'}</span>
        </button>
        <span>{t('title-header') || 'Assets'}</span>
        <i className="now-ui-icons arrows-1_minimal-right" style={{ fontSize: '0.7rem' }} />
        <span style={{ color: '#2c2c2c', fontWeight: 600, letterSpacing: '0.04em' }}>
          {isEditing ? (assetData.identifier || assetData._id) : (t('new-asset') || 'New asset')}
        </span>
      </div>

      {/* Hero */}
      {isEditing && (
        <Card className="kit-form-hero">
          <CardBody>
            <div className="kit-form-hero-flex">
              <div className="kit-form-hero-icon">
                <i className={icon} />
              </div>
              <div className="kit-form-hero-titles">
                <div className="kit-form-hero-friendly" style={{ fontSize: '1.4rem' }}>
                  {assetData.brand} <span style={{ color: '#8a92a6', fontWeight: 500 }}>· {assetData.model}</span>
                </div>
                <div className="kit-form-hero-sub">
                  <div>
                    <span className="kit-form-hero-label">{t('asset-id') || 'Asset ID'}</span>
                    <code>{assetData._id}</code>
                    <button
                      type="button"
                      className="kit-detail-copy-btn"
                      onClick={(e) => copyId(e, assetData._id)}
                      id="copy-asset-id"
                      aria-label="Copy"
                    >
                      <i className="now-ui-icons files_single-copy-04" />
                    </button>
                    <UncontrolledTooltip target="copy-asset-id">{t('copy-btn') || 'Copy ID'}</UncontrolledTooltip>
                  </div>
                  {assetData.identifier && (
                    <div>
                      <span className="kit-form-hero-label">{t('identifier') || 'Identifier'}</span>
                      <code>{assetData.identifier}</code>
                    </div>
                  )}
                  {assetData.serial_number && (
                    <div>
                      <span className="kit-form-hero-label">{t('serial') || 'Serial'}</span>
                      <code>{assetData.serial_number}</code>
                    </div>
                  )}
                  {assetData.created_at && (
                    <div>
                      <i className="now-ui-icons ui-1_calendar-60" style={{ color: '#1c8ee0', marginRight: '0.3rem' }} />
                      <span>{moment(assetData.created_at).format('DD-MM-YYYY HH:mm:ss')}</span>
                    </div>
                  )}
                  {assetData.kit_id && (
                    <div >
                      <span className="kit-form-hero-label">{'Kit ID'}</span>
                      <code>{assetData.kit_id}</code>

                      <button
                        type="button"
                        className="kit-detail-copy-btn"
                        onClick={(e) => copyId(e, assetData.kit_id)}
                        id="copy-kit-id"
                        aria-label="Copy"
                      >
                        <i className="now-ui-icons files_single-copy-04" />
                      </button>

                      <button
                        type="button"
                        className="kit-detail-copy-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          history.push(`/admin/kits/patients/details/${assetData.kit_id}`);
                        }}
                        id="kit-load-button"
                        aria-label="Go to kit"
                      >
                        <i className="now-ui-icons ui-1_zoom-bold" />
                      </button>

                      <UncontrolledTooltip target="copy-kit-id">{t('copy-btn') || 'Copy ID'}</UncontrolledTooltip>
                      <UncontrolledTooltip target="kit-load-button">{t('inspect-kit') || 'Inspect kit'}</UncontrolledTooltip>

                      
                    </div>
                  )}
                </div>
              </div>
              <Badge color={tone} pill style={{
                fontSize: '0.78rem',
                padding: '0.5rem 0.9rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
              }}>
                {t(assetData.status)}
              </Badge>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Form — un único card. En edición: tabset Información / Actividad. */}
      <Form id="asset-form" onSubmit={handleSubmit(onSubmit)}>
        <Card className="kit-form-card">
          <CardBody>
            {isEditing ? (
              <div className="kit-form-tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === 'info'}
                  className={`kit-form-tab ${tab === 'info' ? 'is-active' : ''}`}
                  onClick={() => setTab('info')}
                >
                  <i className="now-ui-icons ui-2_settings-90" />
                  <span>{t('tab-info') || 'Información'}</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === 'log'}
                  className={`kit-form-tab ${tab === 'log' ? 'is-active' : ''}`}
                  onClick={() => setTab('log')}
                >
                  <i className="now-ui-icons ui-2_time-alarm" />
                  <span>{t('activity-log')}</span>
                </button>
              </div>
            ) : (
              <div className="kit-form-card-header">
                <i className="now-ui-icons ui-2_settings-90" />
                <span>{t('asset-info') || 'Asset Info'}</span>
              </div>
            )}

            {/* Panel: Información (alta o tab info) */}
            {(!isEditing || tab === 'info') && (
              <div className="kit-form-tabpanel">
                <div className="kit-form-fields">
                  <div>
                    <Label for="asset_type">{t('type')}</Label>
                    <Input
                      disabled={assetData.readOnly}
                      type="select"
                      name="asset_type"
                      id="asset_type"
                      defaultValue={assetData.asset_type || 'cellphone'}
                      onChange={(e) => setAssetType(e.target.value)}
                      innerRef={register}
                    >

                      {Object.keys(types).map((key, i) => (
                          <option key={`type-${i}`} value={types[key].value}>{t(key)}</option>
                      ))}
                    </Input>
                  </div>

                  <div>
                    <Label for="status">{t('status')}</Label>
                    <Input
                      disabled={assetData.readOnly}
                      type="select"
                      name="status"
                      id="status"
                      defaultValue={assetData.status}
                      innerRef={register}
                    >
                      {!isEditing ? (
                        <>
                          <option value="a_st_available">{t('a_st_available')}</option>
                          <option value="a_st_unavailable">{t('a_st_unavailable')}</option>
                        </>
                      ) : (
                        Object.keys(stateMachine).length && stateMachine[assetData.status]
                          ? stateMachine[assetData.status].map((st) => (<option key={st} value={st}>{t(st)}</option>))
                          : null
                      )}
                    </Input>
                  </div>

                  {assetType === 'device' && (
                    <div>
                      <Label for="study_type">{t('study-type') || 'Study type'}</Label>
                      <Input
                        disabled={assetData.readOnly}
                        type="select"
                        name="study_type"
                        id="study_type"
                        defaultValue={assetData.study_type}
                        innerRef={register}
                      >
                        {Object.keys(studyTypes).map((key, i) => (
                          <option key={`type-${i}`} value={studyTypes[key]}>{studyTypes[key]}</option>
                        ))}
                      </Input>
                    </div>
                  )}

                  <div>
                    <Label for="brand">{t('brand')}</Label>

                    <Input
                      disabled={assetData.readOnly}
                      type="select"
                      defaultValue={assetData.brand}
                      onChange={(e) => { 
                        const brand = brands.find(b => b.name === e.target.value) ?? [];
                        setSelectedBrand(brand);
                        document.getElementById("brand").value = e.target.value;
                        document.getElementById("model").value = brand.models[0].name;
                      }}
                    >
                      {brands.map((br, i) => (
                        <option key={`brand-${i}`} value={br.name}>{br.name}</option>
                      ))}
                    </Input>

                    <Input type="hidden" disabled={assetData.readOnly} name="brand" id="brand" innerRef={register} />
                  </div>
                  <div>
                    <Label for="model">{t('model')}</Label>

                    <Input
                      disabled={assetData.readOnly}
                      type="select"
                      defaultValue={assetData.model}
                      onChange={(e) => { 
                        document.getElementById("model").value = e.target.value;
                      }}
                    >
                      {!selectedBrand || !Object.keys(selectedBrand).length ? <option value="-">-</option> : selectedBrand.models.map((br, i) => (
                        <option key={`model-${selectedBrand.name}-${i}`} value={br.name}>{br.name}</option>
                      ))}
                    </Input>
                    
                    <Input type="hidden" disabled={assetData.readOnly} name="model" id="model" innerRef={register} />
                  </div>
                  <div>
                    <Label for="serial_number">{t('serial')}</Label>
                    <Input type="text" disabled={assetData.readOnly} name="serial_number" id="serial_number" innerRef={register} />
                  </div>
                  <div>
                    <Label for="identifier">{t('identifier')}</Label>
                    <Input type="text" disabled={assetData.readOnly} name="identifier" id="identifier" innerRef={register} />
                  </div>

                  <div id="asset-form-checkbox">
                    <span className="label-style">{t('returnable')}</span>
                    <div className="asset-form-checkbox">
                      <Input type="checkbox" disabled={assetData.readOnly} name="returnable" id="returnable" innerRef={register} />
                    </div>
                    <UncontrolledTooltip target={`asset-form-checkbox`} placement="bottom">{t("checkbox-explain")}</UncontrolledTooltip>
                  </div>
                  
                </div>

                <div style={{ marginTop: '1rem' }}>
                  <Label for="features">{t('features')}</Label>
                  <Input
                    disabled={assetData.readOnly}
                    type="textarea"
                    rows={4}
                    name="features"
                    id="features"
                    innerRef={register}
                    style={{ borderRadius: 8, fontSize: '0.9rem' }}
                  />
                </div>
              </div>
            )}

            {/* Panel: Actividad */}
            {isEditing && tab === 'log' && (
              <div className="kit-form-tabpanel">
                <ActivityTimeline tracking={assetData.statesTracking} t={t} />
              </div>
            )}

            {/* Footer de acciones (siempre visible) */}
            <div className="kit-form-actions">
              <Button color="secondary" outline type="reset" onClick={(e) => { e.preventDefault(); history.goBack(); }}>
                <i className="now-ui-icons arrows-1_minimal-left" />
                {assetData.readOnly ? t('go-back') : t('cancel-btn') || 'Cancel'}
              </Button>
              {!assetData.readOnly ? (
                <Button form="asset-form" color="primary" type="submit">
                  <i className="now-ui-icons ui-1_check" />
                  {isEditing ? (t('update-btn') || 'Update') : (t('save-btn') || 'Save')}
                </Button>
              ) : <></>}
            </div>
          </CardBody>
        </Card>
      </Form>
    </div>
  );
};

export default translate('assets')(AssetsForm);
